import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';

import {HttpClient} from '@angular/common/http';

import {AppComponent} from './app.component';

import {NgxPermissionsModule} from 'ngx-permissions';
import {HttpLoaderFactory, SharedModule} from './shared.module';
import * as Sentry from '@sentry/browser';
import {appVersion, environment} from '../environments/environment';
import {GeneralErrorHandleService} from './core/general-error-handle.service';
import {LOCATION_INITIALIZED} from '@angular/common';
import {UserService} from './services/user.service';
import {User} from '../models/user';
import {ObjectUtils} from './utils/object.utils';
import {ReferenceDataService} from './services/reference-data/reference-data.service';
import {WebUtils} from './utils/web.utils';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {NgbDateCustomParserFormatter} from './core/achoo-datepicker/ngb-date-custom-parser-formatter';
import {CustomReuseStrategy} from './routing';
import {RouteReuseStrategy} from '@angular/router';
import {DeviceDetectorModule} from 'ngx-device-detector';
import {CommonConstant} from './app.constants';

Sentry.init({
  dsn: environment.sentry,
  release: appVersion,
  environment: environment.production ? 'production' : 'development',
  ignoreErrors: [
    /^not found platform setting for account id/,
    /^You must select at least 1 filter to generate a report/,
    /^add_new_campaign_error/,
    /^update_campaign_error/,
    /^Non-Error exception captured with keys: error, headers, message, name, ok/,
    /We’re unable to add your selection to the group. All of the [0-9]+ seats allowed for your account are currently in use./,
    /You are trying to add [0-9]+ influencers to a group, but you only have [0-9]+ slots available./,
    /504 Gateway Timeout/
  ],
  async beforeSend(event) {
    let customEvent = event;
    customEvent = await addSwLog(event);
    return customEvent;
  }
});

async function addSwLog(event) {
  try {
    if (environment.production) {
      const swDebuggerLog = await fetch(`${environment.hostName}/ngsw/state`);
      let swLogContent = await swDebuggerLog.text();
      if (swLogContent.length > CommonConstant.MAX_LOG_LENGTH) {
        swLogContent = swLogContent.substring(0, CommonConstant.MAX_LOG_LENGTH);
      }
      event.breadcrumbs.push({
        category: 'sw_log',
        message: swLogContent
      });
      console.log(`event.breadcrumbs: ${event.breadcrumbs.length}`);
    }
    return event;
  } catch (err) {
    return event;
  }
}

/**
 * define factory which use to load data or configuration when initialize application
 * @param {TranslateService} translate
 * @param {Injector} injector
 * @returns {() => Promise<any>}
 */
export function appInitializerFactory(translate: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const lang = 'en';
      translate.setDefaultLang(lang);
      translate.use(lang).subscribe(() => {
        resolve(true);
      }, err => {
        Sentry.captureException(new Error(`Problem with '${lang}' language initialization.'`));
      }, () => {
        resolve(null);
      });
    });
  });
}

export function getCompanyFactory(injector: Injector): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!localStorage.getItem('user')) {
        resolve();
      } else {
        const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
        if (user !== null) {
          // workaround to prevent cyclic dependency issue.
          // Refer: https://github.com/angular/angular/issues/18224
          setTimeout(() => {
            const userService: UserService = injector.get(UserService);
            const activeAccount = WebUtils.extractActiveAccountFromUrl();
            userService.loadAccountListByUserTask(user, activeAccount)
              .subscribe({
                next: resolve,
                error: resolve
              });
          });
        }
      }
    });
  };
}

export function loadReferenceDataFactory(injector: Injector): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise((resolve, reject) => {
      // workaround to prevent cyclic dependency issue.
      // Refer: https://github.com/angular/angular/issues/18224
      setTimeout(() => {
        const referenceService: ReferenceDataService = injector.get(ReferenceDataService);
        referenceService.loadDefinitions().subscribe((res) => {
          referenceService.updateDefinitionsData(res.data);
          resolve();
        }, (error) => {
          resolve();
        });
      });
    });
  };
}


@NgModule({
    declarations: [],
    imports: [
      BrowserModule,
      SharedModule,
      NgxPermissionsModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      DeviceDetectorModule.forRoot()
    ],
    providers: [
      {
        provide: APP_INITIALIZER,
        useFactory: appInitializerFactory,
        deps: [TranslateService, Injector],
        multi: true
      },
      {
        provide: APP_INITIALIZER,
        useFactory: getCompanyFactory,
        deps: [Injector],
        multi: true
      },
      {
        provide: APP_INITIALIZER,
        useFactory: loadReferenceDataFactory,
        deps: [Injector],
        multi: true
      },
      [{provide: ErrorHandler, useClass: GeneralErrorHandleService }],
      {provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter},
      { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts